import React, { useState, useEffect, useContext } from 'react';
import { Box, Card, CardContent, Typography, Grid, Chip, CircularProgress, Alert, Button, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
  Divider, Avatar, IconButton, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Edit, Save, Security, People, CheckCircle, Cancel, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';


// Password strength checker
const getPasswordStrength = (pw) => {
  if (!pw) return { label: '', color: '', score: 0 };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score <= 2) return { label: 'Weak', color: '#f44336', score };
  if (score <= 3) return { label: 'Medium', color: '#ff9800', score };
  return { label: 'Strong', color: '#4caf50', score };
};
const API = '/api';
const RC = {admin:'#E8622A', telecaller:'#2196F3', salesperson:'#4CAF50'};
const CAT_COLORS = {branding:'#E8622A',contact:'#2196F3',calls:'#4CAF50',display:'#9C27B0',security:'#F44336',system:'#607D8B',leads:'#FF9800',appointments:'#00BCD4',recalls:'#795548'};

const ALL_PERMS = {
  leads:['leads.view','leads.create','leads.edit','leads.delete'],
  appointments:['appointments.view','appointments.create','appointments.edit','appointments.delete'],
  calls:['calls.view','calls.create','calls.edit'],
  recalls:['recalls.view','recalls.create','recalls.edit','recalls.delete'],
  users:['users.view','users.create','users.edit','users.delete','users.role'],
  team:['team.view','team.manage'],
  reports:['reports.view','reports.export'],
  settings:['settings.view','settings.edit'],
  dashboard:['dashboard.view','dashboard.export'],
};
const MOD_COLORS = {leads:'#2196F3',appointments:'#E8622A',calls:'#4CAF50',recalls:'#FF9800',users:'#9C27B0',team:'#00BCD4',reports:'#607D8B',settings:'#795548',dashboard:'#F44336'};

function PermissionEditor({perm, onSave}) {
  const MODULES = [
    {key:'dashboard',label:'Dashboard'},
    {key:'preleads',label:'Pre-Leads'},
    {key:'calendar',label:'Calendar'},
    {key:'appointments',label:'Appointments'},
    {key:'recalls',label:'Recall Queue'},
    {key:'team',label:'Team Management'},
    {key:'masterdata',label:'Master Data'},
    {key:'callreport',label:'Call Report'},
    {key:'reports',label:'Reports'},
    {key:'solarrgf',label:'Solar RGF Form'},
    {key:'settings',label:'Settings'},
  ];
  const ACTIONS = ['view','add','edit','delete','assign','reassign','export'];
  const [perms, setPerms] = useState({});
  const [changed, setChanged] = useState(false);

  useEffect(()=>{
    const p = {};
    MODULES.forEach(m=>{ p[m.key]={}; ACTIONS.forEach(a=>{ p[m.key][a]=false; }); });
    if(Array.isArray(perm.permissions)) {
      perm.permissions.forEach(pr=>{
        if(typeof pr==='string'){ const[mod,act]=pr.split(':'); if(p[mod]) p[mod][act]=true; }
      });
    }
    setPerms(p); setChanged(false);
  },[perm._id, perm.role]);

  const toggle=(mod,act)=>{ setPerms(prev=>({...prev,[mod]:{...prev[mod],[act]:!prev[mod][act]}})); setChanged(true); };

  const handleSave=()=>{
    const arr=[];
    Object.keys(perms).forEach(mod=>Object.keys(perms[mod]).forEach(act=>{ if(perms[mod][act]) arr.push(mod+':'+act); }));
    onSave(perm.role,arr); setChanged(false);
  };

  const RC={superadmin:'#6a0000',admin:'#003e6f',telecaller:'#1565C0',salesperson:'#2E7D32'};
  const roleLabel = perm.role.replace('_',' ').charAt(0).toUpperCase()+perm.role.replace('_',' ').slice(1);

  return (
    <Box sx={{border:'1px solid #e0e7ff',borderRadius:2,overflow:'hidden',mb:2}}>
      <Box sx={{bgcolor:RC[perm.role]||'#003e6f',px:2,py:1.5,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <Typography fontWeight={700} color="white" fontSize={14}>{roleLabel}</Typography>
        <Box display="flex" gap={1} alignItems="center">
          {changed&&<Typography fontSize={11} sx={{color:'#FCB812',fontWeight:600}}>● Unsaved changes</Typography>}
          <Button size="small" variant="contained" onClick={handleSave}
            sx={{bgcolor:'#FCB812',color:'#003e6f','&:hover':{bgcolor:'#e6a800'},fontWeight:700,fontSize:11,py:0.3}}>
            Save & Apply
          </Button>
        </Box>
      </Box>
      <Box sx={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr>
              <th style={{padding:'8px 12px',textAlign:'left',borderBottom:'1px solid #e0e7ff',minWidth:140,backgroundColor:'#f5f7fa'}}>Module</th>
              {ACTIONS.map(a=><th key={a} style={{padding:'8px',textAlign:'center',borderBottom:'1px solid #e0e7ff',color:'#555',minWidth:65,backgroundColor:'#f5f7fa'}}>{a.charAt(0).toUpperCase()+a.slice(1)}</th>)}
            </tr>
          </thead>
          <tbody>
            {MODULES.map((m,mi)=>(
              <tr key={m.key} style={{backgroundColor:mi%2===0?'#fafafa':'white'}}>
                <td style={{padding:'6px 12px',fontWeight:500,color:'#333',borderBottom:'1px solid #f0f0f0'}}>{m.label}</td>
                {ACTIONS.map(a=>(
                  <td key={a} style={{padding:'6px 8px',textAlign:'center',borderBottom:'1px solid #f0f0f0'}}>
                    <input type="checkbox" checked={!!(perms[m.key]&&perms[m.key][a])} onChange={()=>toggle(m.key,a)}
                      style={{cursor:'pointer',width:16,height:16,accentColor:RC[perm.role]||'#003e6f'}}/>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}


export default function Settings() {
  const {user, fetchPermissions} = useContext(AuthContext);
  const isAdmin = user?.role==='admin' || user?.role==='superadmin';
  const isSuperAdmin = user?.role==='superadmin';
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile
  const [profile, setProfile] = useState({name:'',phone:'',email:''});
  const [passwords, setPasswords] = useState({current:'',newPw:'',confirm:''});
  const [showPw, setShowPw] = useState({current:false,newPw:false,confirm:false});
  const [saving, setSaving] = useState(false);

  // Users
  const [users, setUsers] = useState([]);
  const [userOpen, setUserOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [userForm, setUserForm] = useState({name:'',email:'',password:'',role:'telecaller',phone:''});
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState('');
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdUser, setPwdUser] = useState(null);
  const [newPwd, setNewPwd] = useState('');

  // Configs
  const [configs, setConfigs] = useState([]);
  const [editConfig, setEditConfig] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [configSaving, setConfigSaving] = useState(false);

  // Permissions
  const [permissions, setPermissions] = useState([]);

  const hdr = {headers:{Authorization:'Bearer '+localStorage.getItem('token')}};

  const loadProfile = async () => {
    try {
      const r = await axios.get(API+'/users/profile', hdr);
      const u = r.data.user;
      setProfile({name:u?.name||'',phone:u?.phone||'',email:u?.email||''});
    } catch(e) { console.error(e); }
  };

  const loadUsers = async () => {
    try {
      const r = await axios.get(API+'/users', hdr);
      const allUsers = r.data.users||[];
      setUsers(isSuperAdmin ? allUsers : allUsers.filter(u=>u.role!=='superadmin'));
    } catch(e) { console.error(e); }
  };

  const loadConfigs = async () => {
    try {
      const r = await axios.get(API+'/settings', hdr);
      setConfigs(r.data.configs||[]);
    } catch(e) { console.error(e); }
  };

  const loadPermissions = async () => {
    try {
      const r = await axios.get(API+'/settings/roles/permissions', hdr);
      setPermissions(r.data.permissions||[]);
    } catch(e) { console.error(e); }
  };

  useEffect(()=>{ loadProfile(); },[]);
  useEffect(()=>{
    if(tab===0) loadProfile();
    if(tab===2 && isAdmin) loadUsers();
    if(tab===3 && isSuperAdmin) loadConfigs();
    if(tab===3 && isAdmin && !isSuperAdmin) loadPermissions();
    if(tab===4 && isAdmin) loadPermissions();
  },[tab]);

  const grouped = configs.reduce((acc,c)=>{ if(!acc[c.category]) acc[c.category]=[]; acc[c.category].push(c); return acc; },{});

  const saveProfile = async () => {
    try { setSaving(true); setError(''); setSuccess('');
      await axios.put(API+'/users/profile',{name:profile.name,phone:profile.phone},hdr);
      setSuccess('Profile updated!'); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if(!passwords.current) return setError('Current password required');
    if(passwords.newPw.length<6) return setError('Min 6 characters');
    if(passwords.newPw!==passwords.confirm) return setError('Passwords do not match');
    try { setSaving(true); setError(''); setSuccess('');
      await axios.put(API+'/users/profile',{password:passwords.newPw},hdr);
      setPasswords({current:'',newPw:'',confirm:''});
      setSuccess('Password changed!'); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError(e.response?.data?.message||'Failed'); }
    finally { setSaving(false); }
  };

  const saveConfig = async () => {
    try { setConfigSaving(true);
      let val=editValue; try{val=JSON.parse(editValue);}catch{}
      await axios.put(API+'/settings/'+editConfig.key,{value:val},hdr);
      setEditConfig(null); loadConfigs();
      setSuccess('Setting updated!'); setTimeout(()=>setSuccess(''),3000);
    } catch(e) { setError(e.response?.data?.message||'Failed'); }
    finally { setConfigSaving(false); }
  };

  const savePermissions = async (role, perms) => {
    try { 
      await axios.put(API+'/settings/roles/'+role,{permissions:perms},hdr);
      setSuccess('Permissions saved for '+role+'! Users must re-login to see changes.');
      setTimeout(()=>setSuccess(''),5000);
      loadPermissions();
      const saveToken = localStorage.getItem('token');
      if(typeof fetchPermissions === 'function' && saveToken) fetchPermissions(user?.role, saveToken);
    } catch(e) { setError(e.response?.data?.message||'Failed'); }
  };

  return (
    <Box p={3}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box><Typography variant="h5" fontWeight={700}>Settings</Typography>
          <Typography variant="body2" color="text.secondary">System configuration, permissions & profile</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh/>} size="small" onClick={()=>{
          if(tab===0) loadProfile();
          if(tab===2&&isAdmin) loadUsers();
          if(tab===3&&isSuperAdmin) loadConfigs();
          if(tab===3&&isAdmin&&!isSuperAdmin) loadPermissions();
          if(tab===4&&isAdmin) loadPermissions();
        }} sx={{borderColor:'#E8622A',color:'#E8622A'}}>Refresh</Button>
      </Box>

      {error&&<Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}
      {success&&<Alert severity="success" sx={{mb:2}} onClose={()=>setSuccess('')}>{success}</Alert>}

      <Paper sx={{borderRadius:2,mb:3}}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{'& .MuiTab-root':{fontWeight:600,textTransform:'none',fontSize:13}}}>
          <Tab label="👤 My Profile"/>
          <Tab label="🔒 Change Password"/>
          {isAdmin&&<Tab label="👥 User Management"/>}
          {isSuperAdmin&&<Tab label="⚙️ System Config"/>}
          {isAdmin&&<Tab label="🛡️ Role Permissions"/>}
        </Tabs>
      </Paper>

      {/* TAB 0: My Profile */}
      {tab===0&&(
        <Card sx={{borderRadius:3}}><CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Avatar sx={{width:72,height:72,bgcolor:RC[user?.role]||'#E8622A',fontSize:28,fontWeight:700}}>
              {user?.name?.charAt(0)?.toUpperCase()||'U'}
            </Avatar>
            <Box><Typography variant="h6" fontWeight={700}>{user?.name}</Typography>
              <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
              <Chip label={user?.role} size="small" sx={{mt:0.5,bgcolor:(RC[user?.role]||'#E8622A')+'20',color:RC[user?.role]||'#E8622A',fontWeight:600}}/>
            </Box>
          </Box>
          <Divider sx={{mb:3}}/>
          <Grid container spacing={2} maxWidth={600}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Full Name" value={profile.name} onChange={e=>setProfile({...profile,name:e.target.value})} size="small"/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Email" value={profile.email} disabled size="small" helperText="Cannot be changed"/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Phone" value={profile.phone} onChange={e=>setProfile({...profile,phone:e.target.value})} size="small"/></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Role" value={user?.role} disabled size="small" helperText="Changed by admin only"/></Grid>
            <Grid item xs={12}><Button variant="contained" startIcon={<Save/>} onClick={saveProfile} disabled={saving}
              sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
              {saving?<CircularProgress size={18} sx={{color:'white'}}/>:'Save Profile'}</Button></Grid>
          </Grid>
        </CardContent></Card>
      )}

      {/* TAB 1: Change Password */}
      {tab===1&&(
        <Card sx={{borderRadius:3}}><CardContent>
          <Typography variant="h6" fontWeight={700} mb={2}>🔒 Change Password</Typography>
          <Divider sx={{mb:3}}/>
          <Grid container spacing={2} maxWidth={500}>
            <Grid item xs={12}><TextField fullWidth label="Current Password" type={showPw.current?'text':'password'} value={passwords.current} onChange={e=>setPasswords({...passwords,current:e.target.value})} size="small" InputProps={{endAdornment:<InputAdornment position="end"><IconButton size="small" onClick={()=>setShowPw(s=>({...s,current:!s.current}))}>{showPw.current?<span style={{fontSize:11}}>HIDE</span>:<span style={{fontSize:11}}>SHOW</span>}</IconButton></InputAdornment>}}/></Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="New Password" type={showPw.newPw?'text':'password'} value={passwords.newPw} onChange={e=>setPasswords({...passwords,newPw:e.target.value})} size="small" InputProps={{endAdornment:<InputAdornment position="end"><IconButton size="small" onClick={()=>setShowPw(s=>({...s,newPw:!s.newPw}))}>{showPw.newPw?<span style={{fontSize:11}}>HIDE</span>:<span style={{fontSize:11}}>SHOW</span>}</IconButton></InputAdornment>}}/>
              {passwords.newPw && (()=>{const s=getPasswordStrength(passwords.newPw);return(<Box mt={0.5} display="flex" alignItems="center" gap={1}><Box sx={{height:4,flex:1,borderRadius:2,bgcolor:'#eee'}}><Box sx={{height:'100%',width:(s.score/5*100)+'%',bgcolor:s.color,borderRadius:2,transition:'all 0.3s'}}/></Box><Typography variant="caption" sx={{color:s.color,fontWeight:600,minWidth:50}}>{s.label}</Typography></Box>)})()}
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Confirm New Password" type={showPw.confirm?'text':'password'} value={passwords.confirm} onChange={e=>setPasswords({...passwords,confirm:e.target.value})} size="small"
              error={!!passwords.confirm&&passwords.newPw!==passwords.confirm} helperText={!!passwords.confirm&&passwords.newPw!==passwords.confirm?'Passwords do not match':''}
              InputProps={{endAdornment:<InputAdornment position="end"><IconButton size="small" onClick={()=>setShowPw(s=>({...s,confirm:!s.confirm}))}>{showPw.confirm?<span style={{fontSize:11}}>HIDE</span>:<span style={{fontSize:11}}>SHOW</span>}</IconButton></InputAdornment>}}/></Grid>
            <Grid item xs={12}><Button variant="contained" startIcon={<Security/>} onClick={changePassword} disabled={saving}
              sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
              {saving?<CircularProgress size={18} sx={{color:'white'}}/>:'Change Password'}</Button></Grid>
          </Grid>
        </CardContent></Card>
      )}

      {/* TAB 2: User Management (admin only) */}
      {isAdmin&&tab===2&&(
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box><Typography variant="h6" fontWeight={700}>User Management</Typography>
              <Typography variant="body2" color="text.secondary">{users.length} total users</Typography>
            </Box>
            <Button variant="contained" startIcon={<People/>}
              onClick={()=>{setEditUser(null);setUserForm({name:'',email:'',password:'',role:'telecaller',phone:''});setUserError('');setUserOpen(true);}}
              sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
              Add New User
            </Button>
          </Box>
          {users.length===0
            ?<Card sx={{borderRadius:2}}><CardContent sx={{textAlign:'center',py:6}}>
                <People sx={{fontSize:48,color:'#ddd',mb:2}}/>
                <Typography color="text.secondary">No users found</Typography>
                <Button variant="contained" sx={{mt:2,bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none'}}
                  onClick={()=>{setEditUser(null);setUserForm({name:'',email:'',password:'',role:'telecaller',phone:''});setUserError('');setUserOpen(true);}}>
                  Create First User
                </Button>
              </CardContent></Card>
            :<TableContainer component={Paper} elevation={1} sx={{borderRadius:2}}>
              <Table>
                <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:700,fontSize:12,color:'#555'}}}>
                  <TableCell>#</TableCell><TableCell>Name</TableCell><TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell><TableCell>Role</TableCell><TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell><TableCell>Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {users.map((u,i)=>(
                    <TableRow key={u._id} hover sx={{'&:hover':{bgcolor:'#FFF8F5'}}}>
                      <TableCell sx={{color:'#999',fontSize:11}}>{i+1}</TableCell>
                      <TableCell><Box display="flex" alignItems="center" gap={1}>
                        <Avatar sx={{bgcolor:RC[u.role]||'#E8622A',width:32,height:32,fontSize:13,fontWeight:700}}>{(u.name||u.email||'U').charAt(0).toUpperCase()}</Avatar>
                        <Typography variant="body2" fontWeight={500}>{u.name||u.email}</Typography>
                      </Box></TableCell>
                      <TableCell sx={{fontSize:12,color:'#2196F3'}}>{u.email}</TableCell>
                      <TableCell sx={{fontSize:12}}>{u.phone||'—'}</TableCell>
                      <TableCell><Chip label={u.role} size="small" sx={{bgcolor:(RC[u.role]||'#E8622A')+'20',color:RC[u.role]||'#E8622A',fontWeight:600,fontSize:11}}/></TableCell>
                      <TableCell><Chip label={u.isActive?'Active':'Inactive'} size="small" sx={{bgcolor:u.isActive?'#4CAF5020':'#F4433620',color:u.isActive?'#4CAF50':'#F44336',fontSize:11}}/></TableCell>
                      <TableCell sx={{fontSize:11,color:'#999'}}>{u.createdAt?new Date(u.createdAt).toLocaleDateString():'—'}</TableCell>
                      <TableCell><Box display="flex" gap={0.5}>
                        <Tooltip title="Edit"><IconButton size="small" onClick={()=>{setEditUser(u);setUserForm({name:u.name||'',email:u.email||'',password:'',role:u.role||'telecaller',phone:u.phone||''});setUserError('');setUserOpen(true);}} sx={{color:'#2196F3'}}><Edit sx={{fontSize:16}}/></IconButton></Tooltip>
                        <Tooltip title="Change Password"><IconButton size="small" onClick={()=>{setPwdUser(u);setNewPwd('');setPwdOpen(true);}} sx={{color:'#FF9800'}}><Security sx={{fontSize:16}}/></IconButton></Tooltip>
                        <Tooltip title={u.isActive?'Deactivate':'Activate'}>
                          <IconButton size="small" onClick={async()=>{
                            try{await axios.put(API+'/users/'+u._id,{isActive:!u.isActive},hdr);setSuccess((u.isActive?'Deactivated: ':'Activated: ')+(u.name||u.email));loadUsers();setTimeout(()=>setSuccess(''),3000);}
                            catch(e){setError(e.response?.data?.message||'Failed');}
                          }} sx={{color:u.isActive?'#F44336':'#4CAF50'}}>
                            {u.isActive?<Cancel sx={{fontSize:16}}/>:<CheckCircle sx={{fontSize:16}}/>}
                          </IconButton>
                        </Tooltip>
                      </Box></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>}

          {/* Add/Edit User Dialog */}
          <Dialog open={userOpen} onClose={()=>setUserOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
            <DialogTitle fontWeight={700}>{editUser?'Edit User':'Add New User'}</DialogTitle>
            <DialogContent>
              {userError&&<Alert severity="error" sx={{mb:2,mt:1}}>{userError}</Alert>}
              <Box display="flex" flexDirection="column" gap={2} mt={1}>
                <Box display="flex" gap={2}>
                  <TextField fullWidth label="Full Name *" value={userForm.name} onChange={e=>setUserForm({...userForm,name:e.target.value})} size="small"/>
                  <TextField fullWidth label="Phone" value={userForm.phone} onChange={e=>setUserForm({...userForm,phone:e.target.value})} size="small"/>
                </Box>
                <TextField fullWidth label="Email *" value={userForm.email} onChange={e=>setUserForm({...userForm,email:e.target.value})} size="small" disabled={!!editUser} helperText={editUser?'Email cannot be changed':''}/>
                {!editUser&&<TextField fullWidth label="Password *" type="password" value={userForm.password} onChange={e=>setUserForm({...userForm,password:e.target.value})} size="small" helperText="Min 6 characters"/>}
                <FormControl fullWidth size="small">
                  <InputLabel>Role *</InputLabel>
                  <Select value={userForm.role} label="Role *" onChange={e=>setUserForm({...userForm,role:e.target.value})}>
                    <MenuItem value="admin"><Box display="flex" alignItems="center" gap={1}><Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:'#E8622A'}}/>Admin — Full access</Box></MenuItem>
                    <MenuItem value="telecaller"><Box display="flex" alignItems="center" gap={1}><Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:'#2196F3'}}/>Telecaller — Leads and calls</Box></MenuItem>
                    <MenuItem value="salesperson"><Box display="flex" alignItems="center" gap={1}><Box sx={{width:10,height:10,borderRadius:'50%',bgcolor:'#4CAF50'}}/>Salesperson — Assigned leads</Box></MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{p:2}}>
              <Button onClick={()=>setUserOpen(false)}>Cancel</Button>
              <Button variant="contained" disabled={userSaving} onClick={async()=>{
                if(!userForm.name||!userForm.email) return setUserError('Name and email required');
                if(!editUser&&!userForm.password) return setUserError('Password required');
                if(!editUser&&userForm.password.length<6) return setUserError('Min 6 characters');
                try{setUserSaving(true);setUserError('');
                  if(editUser) await axios.put(API+'/users/'+editUser._id,{name:userForm.name,role:userForm.role,phone:userForm.phone},hdr);
                  else await axios.post(API+'/users',userForm,hdr);
                  setUserOpen(false);setSuccess((editUser?'Updated: ':'Created: ')+userForm.name);
                  loadUsers();setTimeout(()=>setSuccess(''),3000);
                }catch(e){setUserError(e.response?.data?.message||'Failed');}
                finally{setUserSaving(false);}
              }} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
                {userSaving?<CircularProgress size={18} sx={{color:'white'}}/>:(editUser?'Update User':'Create User')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Change Password Dialog */}
          <Dialog open={pwdOpen} onClose={()=>setPwdOpen(false)} maxWidth="xs" fullWidth PaperProps={{sx:{borderRadius:3}}}>
            <DialogTitle fontWeight={700}>Change Password — {pwdUser?.name||pwdUser?.email}</DialogTitle>
            <DialogContent>
              <Box mt={1}><TextField fullWidth label="New Password" type="password" value={newPwd} onChange={e=>setNewPwd(e.target.value)} size="small" helperText="Min 6 characters" autoFocus/></Box>
            </DialogContent>
            <DialogActions sx={{p:2}}>
              <Button onClick={()=>setPwdOpen(false)}>Cancel</Button>
              <Button variant="contained" onClick={async()=>{
                if(newPwd.length<6) return setError('Min 6 characters');
                try{await axios.put(API+'/users/'+pwdUser._id,{password:newPwd},hdr);
                  setPwdOpen(false);setNewPwd('');
                  setSuccess('Password changed for '+(pwdUser?.name||pwdUser?.email));
                  setTimeout(()=>setSuccess(''),3000);
                }catch(e){setError(e.response?.data?.message||'Failed');}
              }} sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none',fontWeight:600}}>
                Change Password
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* TAB 3: System Config (admin only) */}
      {isSuperAdmin&&tab===3&&isSuperAdmin&&(
        <Box>
          <Alert severity="info" sx={{mb:2}}>These settings control core application behavior. Changes take effect immediately.</Alert>
          {Object.keys(grouped).length===0
            ?<Card sx={{borderRadius:2}}><CardContent sx={{textAlign:'center',py:4}}><Typography color="text.secondary">No configurations found. Click Refresh.</Typography></CardContent></Card>
            :Object.entries(grouped).map(([category,items])=>(
            <Card key={category} sx={{borderRadius:2,mb:2}}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Box sx={{width:12,height:12,borderRadius:'50%',bgcolor:CAT_COLORS[category]||'#607D8B'}}/>
                  <Typography variant="h6" fontWeight={700} sx={{textTransform:'capitalize'}}>{category}</Typography>
                  <Chip label={items.length} size="small" sx={{bgcolor:'#F5F7FA'}}/>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow sx={{'& th':{bgcolor:'#F5F7FA',fontWeight:600,fontSize:12}}}>
                      <TableCell>Key</TableCell><TableCell>Value</TableCell><TableCell>Description</TableCell><TableCell>Visibility</TableCell><TableCell>Action</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {items.map(cfg=>(
                        <TableRow key={cfg.key} hover>
                          <TableCell><Typography sx={{fontFamily:'monospace',fontSize:12,color:'#E8622A'}}>{cfg.key}</Typography></TableCell>
                          <TableCell>
                            {typeof cfg.value==='boolean'
                              ?<Chip label={String(cfg.value)} size="small" sx={{bgcolor:cfg.value?'#4CAF5020':'#F4433620',color:cfg.value?'#4CAF50':'#F44336'}}/>
                              :Array.isArray(cfg.value)
                                ?<Typography sx={{fontSize:11,color:'#666'}}>[{cfg.value.join(', ')}]</Typography>
                                :<Typography sx={{fontSize:13,fontWeight:500}}>{String(cfg.value)}</Typography>}
                          </TableCell>
                          <TableCell sx={{fontSize:12,color:'#666',maxWidth:200}}>{cfg.description}</TableCell>
                          <TableCell><Chip label={cfg.isPublic?'Public':'Private'} size="small" sx={{bgcolor:cfg.isPublic?'#2196F320':'#9E9E9E20',color:cfg.isPublic?'#2196F3':'#9E9E9E',fontSize:10}}/></TableCell>
                          <TableCell>
                            <Tooltip title="Edit"><IconButton size="small" onClick={()=>{setEditConfig(cfg);setEditValue(typeof cfg.value==='object'?JSON.stringify(cfg.value):String(cfg.value));}} sx={{color:'#2196F3'}}><Edit sx={{fontSize:16}}/></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
          <Dialog open={!!editConfig} onClose={()=>setEditConfig(null)} maxWidth="sm" fullWidth PaperProps={{sx:{borderRadius:3}}}>
            <DialogTitle fontWeight={700}>Edit: <span style={{color:'#E8622A',fontFamily:'monospace'}}>{editConfig?.key}</span></DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" mb={2}>{editConfig?.description}</Typography>
              <TextField fullWidth label="Value" value={editValue} onChange={e=>setEditValue(e.target.value)}
                multiline={Array.isArray(editConfig?.value)} rows={Array.isArray(editConfig?.value)?3:1} size="small"
                helperText={Array.isArray(editConfig?.value)?'JSON array format: ["val1","val2"]':''}/>
            </DialogContent>
            <DialogActions sx={{p:2}}>
              <Button onClick={()=>setEditConfig(null)}>Cancel</Button>
              <Button variant="contained" onClick={saveConfig} disabled={configSaving}
                sx={{bgcolor:'#E8622A','&:hover':{bgcolor:'#C4501F'},borderRadius:2,textTransform:'none'}}>
                {configSaving?<CircularProgress size={18} sx={{color:'white'}}/>:'Save Changes'}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* TAB 4: Role Permissions (admin only) */}
      {isAdmin&&(tab===4||(tab===3&&!isSuperAdmin))&&(
        <Box>
          <Alert severity="warning" sx={{mb:2}}>Role permissions define what each user role can access. Changes affect all users with that role.</Alert>
          {permissions.length===0
            ?<Card sx={{borderRadius:2}}><CardContent sx={{textAlign:'center',py:4}}><Typography color="text.secondary">Loading permissions... Click Refresh.</Typography></CardContent></Card>
            :permissions.map(perm=>(
              perm.role !== 'lead_management' ? <PermissionEditor key={perm.role} perm={perm} onSave={savePermissions}/> : null
            ))}
        </Box>
      )}
    </Box>
  );
}
